import posthog, { type PostHog } from 'posthog-js';
import { scrubError, scrubProperties } from './scrub';

/**
 * The second measurement layer of ADR-0021, and everything that layer is
 * allowed to do.
 *
 * The traffic question — how many arrived, how fast the page was — is answered
 * by Cloudflare's edge beacon and no code in this repository. This answers the
 * other one: which button she pressed before she bought, and where she stopped.
 *
 * Every option below is a decision written down in ADR-0022. The defaults are
 * not.
 *
 * **The SDK sits in the entry chunk, and that was measured rather than
 * assumed.** It costs 82 kB gzipped — the main chunk goes from 243 kB to 325 kB
 * — which is real money against §13.8's budget. Loading it with a dynamic
 * `import()` does not help and Rollup says why: `@posthog/react` imports
 * `posthog-js` statically, and every screen imports `usePostHog` from
 * `@posthog/react`, so the module is in the graph however this file asks for
 * it. Splitting it needs a local context replacing that hook in ten screens,
 * which is a larger thing than 82 kB on a page that paints from prerendered
 * HTML before any of this runs.
 */

const TOKEN = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as string | undefined;
const HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined;

/**
 * `undefined` when `VITE_API_BASE_URL` is missing or unparsable. `vite.config.ts`
 * already refuses a production build without it, but this runs in dev and in
 * tests too, and analytics initialising must never depend on that guard having
 * run — same stance as the token/host check below.
 */
function backendHostname(): string | undefined {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!apiBaseUrl) return undefined;
  try {
    return new URL(apiBaseUrl).hostname;
  } catch {
    return undefined;
  }
}

/**
 * Set it in `.env.local` to send your own clicking-around to the real project.
 * Off by default because there is one project, and a developer walking the
 * funnel twenty times a day is indistinguishable from twenty buyers in it.
 */
const DEV_OPT_IN = import.meta.env.VITE_PUBLIC_POSTHOG_DEV === 'true';

/**
 * Same-origin, so an ad blocker has nothing to match. ADR-0021 measured the
 * cost of not doing this: a fifth to two fifths of Brazilian traffic. The path
 * is served by `worker/index.ts`, which exists only in the deployed site —
 * hence the direct host in development.
 */
const PROXY_PATH = '/ingest';

export function analyticsHost(): string | undefined {
  if (import.meta.env.PROD) {
    return `${window.location.origin}${PROXY_PATH}`;
  }
  return HOST;
}

/** True when this build is meant to send anything at all. */
export function analyticsEnabled(): boolean {
  if (!TOKEN || !HOST) {
    return false;
  }
  return import.meta.env.PROD || DEV_OPT_IN;
}

let client: PostHog | undefined;

/**
 * For the two reporters that cannot hold a hook: the class error boundary, and
 * anything outside the React tree. Everything inside it uses `usePostHog`.
 *
 */
export function reportException(error: unknown, extra?: Record<string, unknown>): void {
  client?.captureException(scrubError(error), extra ? scrubProperties(extra) : undefined);
}

let handlersInstalled = false;

/**
 * Covers what `ErrorBoundary` cannot: a render error is already reported by
 * React's own boundary, this is everything else — a rejected promise, a
 * throw inside a timer or event handler outside the component tree.
 */
export function installRuntimeErrorHandlers(): void {
  if (handlersInstalled) return;
  handlersInstalled = true;

  // `posthog` rather than `client`: this runs only from the tail of
  // initAnalytics(), after init(), where the two are the same object —
  // and `client` is not yet assigned when a test calls this directly.
  window.addEventListener('error', (event) => {
    if (event.error) {
      posthog.captureException(scrubError(event.error));
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    // No `event.reason` guard: a rejection with no reason is itself the
    // failure worth seeing, not noise to drop.
    posthog.captureException(scrubError(event.reason));
  });
}

/**
 * Returns the client when it was configured, `undefined` when it was not.
 *
 * A missing token must never stop the product from booting: analytics is the
 * one thing here whose absence should cost a number, never a sale. It is loud
 * in development for the opposite reason — silence there is how a whole
 * quarter of events turns out never to have been sent.
 */
export function initAnalytics(): PostHog | undefined {
  if (!TOKEN || !HOST) {
    if (import.meta.env.DEV) {
      console.error(
        'VITE_PUBLIC_POSTHOG_PROJECT_TOKEN or VITE_PUBLIC_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_PUBLIC_POSTHOG_PROJECT_TOKEN and VITE_PUBLIC_POSTHOG_HOST are configured'
      );
    }
    return undefined;
  }

  if (!analyticsEnabled()) {
    return undefined;
  }

  const backendHost = backendHostname();

  posthog.init(TOKEN, {
    api_host: analyticsHost(),
    // Where the toolbar and the "view in PostHog" links point. Without it they
    // would be built from the proxy path and land on this site.
    ui_host: HOST,
    defaults: '2026-01-30',

    // ADR-0024: lets the backend's exception-capture plugin attach this
    // browser's session id to whatever it captures. Scoped to the service's
    // own domain only — never a wildcard.
    ...(backendHost ? { tracing_headers: [backendHost] } : {}),

    // ADR-0021, and this is the line the PostHog wizard's own instructions ask
    // to leave on. Autocapture records every click and form submit on the page
    // and infers meaning from CSS selectors afterwards, which is the opposite
    // of how the events in this app are chosen. It also captures the text
    // inside the element clicked — on a screen made of prompts she wrote, that
    // is content, not behaviour.
    autocapture: false,

    // Named rather than inherited from `defaults`: this app is a single-page
    // router, and every screen change is a history entry rather than a load.
    capture_pageview: 'history_change',

    // Anonymous events cost a fraction of identified ones and are still linked
    // to her the moment `identify` runs. The sales screen is anonymous by
    // definition and is also where the money is decided, so this is not a
    // corner worth paying full price for.
    person_profiles: 'identified_only',

    session_recording: {
      // Every input and every piece of text on the page, replaced with blocks.
      // What survives is where the cursor went, what she clicked and where she
      // stalled — which is the whole reason to watch a replay — without the
      // recording carrying a single word she typed or bought.
      maskAllInputs: true,
      maskTextSelector: '*'
    }
  });

  client = posthog;
  installRuntimeErrorHandlers();
  return posthog;
}
